/**
 * Upgrade tree (spec sections 17, 34). Each upgrade is a one-time capital
 * purchase that adds a recurring maintenance cost and passive effects the
 * engines read. Costs/effects live here (data); UpgradeEngine applies them.
 */
export interface UpgradeEffects {
  /** Added payment throughput (faster entry/exit/payment). */
  paymentThroughput?: number;
  /**
   * Acts as an automatic entry gate that issues tickets on its own (barrier /
   * LPR), so the lot can admit cars without a cashier at the gate.
   */
  entryAutomation?: boolean;
  /** Flat security added lot-wide (0..100 scale). */
  security?: number;
  /** Fractional demand bonus (e.g. 0.1 = +10%). */
  demandBonus?: number;
  /** Enables EV drivers to visit (they need a charger). */
  evEnabled?: boolean;
  /** Enables automatic demand-based price adjustment. */
  dynamicPricing?: boolean;
}

export interface UpgradeDef {
  id: string;
  name: string;
  cost: number; // one-time (capex)
  maintenanceCost: number; // per month
  requiredLevel: number;
  prerequisites: string[];
  description: string;
  effects: UpgradeEffects;
}

export const UPGRADES: Record<string, UpgradeDef> = {
  automatic_barrier: {
    id: 'automatic_barrier',
    name: 'Automatic Barrier',
    cost: 150_000,
    maintenanceCost: 3_000,
    requiredLevel: 1,
    prerequisites: [],
    description: 'Faster entry/exit throughput; issues tickets at the gate',
    effects: { paymentThroughput: 0.5, entryAutomation: true },
  },
  qr_payment: {
    id: 'qr_payment',
    name: 'QR Payment',
    cost: 80_000,
    maintenanceCost: 1_500,
    requiredLevel: 1,
    prerequisites: [],
    description: 'Speeds up payment',
    effects: { paymentThroughput: 0.5 },
  },
  lpr: {
    id: 'lpr',
    name: 'LPR (Plate Recognition)',
    cost: 250_000,
    maintenanceCost: 4_000,
    requiredLevel: 1,
    prerequisites: ['automatic_barrier'],
    description: 'Ticketless, very fast flow; automatic plate-based entry',
    effects: { paymentThroughput: 1.0, entryAutomation: true },
  },
  cctv: {
    id: 'cctv',
    name: 'CCTV System',
    cost: 60_000,
    maintenanceCost: 1_000,
    requiredLevel: 1,
    prerequisites: [],
    description: 'Raises security',
    effects: { security: 30 },
  },
  ev_charger: {
    id: 'ev_charger',
    name: 'EV Charger',
    cost: 200_000,
    maintenanceCost: 3_000,
    requiredLevel: 1,
    prerequisites: [],
    description: 'Attracts EV drivers (+demand)',
    effects: { evEnabled: true, demandBonus: 0.05 },
  },
  parking_sensor: {
    id: 'parking_sensor',
    name: 'Smart Parking Sensor',
    cost: 180_000,
    maintenanceCost: 2_500,
    requiredLevel: 1,
    prerequisites: [],
    description: 'Guidance convenience (+demand)',
    effects: { demandBonus: 0.1 },
  },
  dynamic_pricing: {
    id: 'dynamic_pricing',
    name: 'Dynamic Pricing',
    cost: 300_000,
    maintenanceCost: 5_000,
    requiredLevel: 1,
    prerequisites: ['qr_payment'],
    description: 'Auto-adjusts price to demand',
    effects: { dynamicPricing: true },
  },
};

/** Display order in the upgrades panel. */
export const UPGRADE_LIST: string[] = [
  'automatic_barrier',
  'qr_payment',
  'lpr',
  'cctv',
  'ev_charger',
  'parking_sensor',
  'dynamic_pricing',
];

export function getUpgrade(id: string): UpgradeDef | undefined {
  return UPGRADES[id];
}
